/* eslint-disable @typescript-eslint/no-explicit-any */
import {CommonModule} from '@angular/common';
import {Component, Input, forwardRef} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

let nextCheckboxId = 0;

@Component({
  selector: 'gatewee-checkbox',
  templateUrl: './gatewee-checkbox.component.html',
  styleUrls: ['./gatewee-checkbox.component.css'],
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GateweeCheckboxComponent),
      multi: true,
    },
  ],
})
export class GateweeCheckboxComponent implements ControlValueAccessor {
  private readonly generatedId = `gatewee-checkbox-${++nextCheckboxId}`;

  @Input() label = '';
  /**
   * Optional `id` on the native checkbox so an external `<label for="...">`
   * (e.g. terms copy beside the box) toggles the control and stays in sync with forms.
   */
  @Input() inputId = '';
  /** Label font size in px (default 16, equivalent to former `gw-text-[16px]`). */
  @Input() labelFontSize: number | string = 16;
  @Input() disabled = false;
  @Input() indeterminate = false;
  /** `'m'` default (20px box); `'s'` compact for dense UIs (e.g. tables). */
  @Input() size: 's' | 'm' = 'm';

  get labelFontSizePx(): number {
    const n = Number(this.labelFontSize);
    return Number.isFinite(n) && n > 0 ? n : 16;
  }

  /** Stable id for the native input (`for` on internal or external labels). */
  get checkboxId(): string {
    return this.inputId || this.generatedId;
  }

  value = false;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: any) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    this.value = value ?? false;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Clicks the native input so a single `change` updates the control. */
  activate(event: Event): void {
    event.preventDefault();
    if (this.disabled) return;
    const input = (event.currentTarget as HTMLElement)
      .closest('.gatewee-checkbox-root')
      ?.querySelector<HTMLInputElement>('input[type="checkbox"]');
    input?.click();
  }

  onNativeInputChange(event: Event): void {
    if (this.disabled) return;
    const checked = (event.target as HTMLInputElement).checked;
    this.value = checked;
    this.onChange(this.value);
    this.onTouched();
  }
}
